import { useState, useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";

export interface SavedNursingHome {
  nh_slug: string;
  center_name: string;
  county_slug: string;
  county_name: string;
  savedAt: string;
  isGuestSave: boolean;
}

const GUEST_SAVES_KEY = "grandpal_guest_saves";

export function useGuestSaves() {
  const { user } = useAuth();
  const [guestSaves, setGuestSaves] = useState<SavedNursingHome[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load guest saves from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(GUEST_SAVES_KEY);
    
    if (saved) {
      try {
        const parsedSaves = JSON.parse(saved);
        setGuestSaves(parsedSaves);
      } catch (error) {
        console.error("Error parsing guest saves:", error);
        localStorage.removeItem(GUEST_SAVES_KEY);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save to localStorage whenever guestSaves changes (but only after initial load)
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(GUEST_SAVES_KEY, JSON.stringify(guestSaves));
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    }
  }, [guestSaves, isLoaded]);

  const isNursingHomeSaved = (nhSlug: string): boolean => {
    return guestSaves.some(save => save.nh_slug === nhSlug);
  };

  const saveNursingHome = (nursingHome: {
    nh_slug: string;
    center_name: string;
    county_slug: string;
    county_name?: string;
  }) => {
    const savedHome: SavedNursingHome = {
      nh_slug: nursingHome.nh_slug,
      center_name: nursingHome.center_name,
      county_slug: nursingHome.county_slug,
      county_name: nursingHome.county_name || nursingHome.county_slug,
      savedAt: new Date().toISOString(),
      isGuestSave: !user
    };

    // Use functional update to ensure we get the latest state
    setGuestSaves(currentSaves => {
      // Check if already exists
      const existingIndex = currentSaves.findIndex(save => save.nh_slug === nursingHome.nh_slug);
      if (existingIndex !== -1) {
        return currentSaves;
      }
      
      return [...currentSaves, savedHome];
    });
  };

  const removeSavedNursingHome = (nhSlug: string) => {
    setGuestSaves(prev => prev.filter(save => save.nh_slug !== nhSlug));
  };

  const getSavedCount = (): number => {
    return guestSaves.length;
  };

  const clearAllSaves = () => {
    setGuestSaves([]);
    localStorage.removeItem(GUEST_SAVES_KEY);
  };

  // Migrate guest saves to user account when they log in
  const migrateGuestSavesToUser = async () => {
    if (!user || guestSaves.length === 0) return;

    // This could be expanded to sync with backend user saves
    // For now, we just mark them as user saves
    setGuestSaves(prev => 
      prev.map(save => ({ ...save, isGuestSave: false }))
    );
  };

  // Auto-migrate when user logs in
  useEffect(() => {
    if (user && guestSaves.some(save => save.isGuestSave)) {
      migrateGuestSavesToUser();
    }
  }, [user, guestSaves.length]); // Use length instead of full array to prevent infinite loops

  return {
    guestSaves,
    isNursingHomeSaved,
    saveNursingHome,
    removeSavedNursingHome,
    getSavedCount,
    clearAllSaves,
    migrateGuestSavesToUser
  };
}